import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const candyShop: TemplateRegistration = {
  meta: {
    id: 'candy-shop',
    name: 'Candy Shop',
    category: 'Creative',
    description: 'Bubbly candy colors with playful staggered card layout',
    previewImage: 'https://images.unsplash.com/photo-1499195333224-3ce974eecb47?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Fredoka', 'Poppins']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const aboutSentences = safeAbout.split(/(?<=[.!?])\s+/);
      const pullQuote = aboutSentences[0] || safeAbout.substring(0, 120);
      sections.push(`
      <section id="about" class="section section-light">
        <div class="container">
          <h2 class="section-title">About Us</h2>
          <div class="about-split">
            <div class="about-pullquote">&ldquo;${pullQuote}&rdquo;</div>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url) => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <p>&ldquo;${tContent}&rdquo;</p>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            <div class="t-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Sweet Words</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-split">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <div class="contact-accent-bar"></div>
            <div class="contact-form-wrap">
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Your Name" required>
                <input type="email" placeholder="Your Email" required>
                <textarea placeholder="Message" rows="4" required></textarea>
                <button type="submit">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #EC4899; --bg: #FFF1F2; --bg-alt: #FFE4E6; --accent: #FBBF24;
      --text: #1F1F1F; --text-muted: #6B6B6B;
      --font-heading: 'Fredoka', sans-serif; --font-body: 'Poppins', sans-serif;
      --shadow-sm: 0 2px 8px rgba(236,72,153,.1); --shadow-md: 0 6px 20px rgba(236,72,153,.15);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,241,242,.95); backdrop-filter: blur(10px); border-bottom: 2px dotted var(--primary); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1100px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--primary); font-weight: 700; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; font-weight: 600; letter-spacing: .5px; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 3px; }

    /* HERO - floating geometric shapes */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--bg); }
    .hero::before { content: ''; position: absolute; width: 180px; height: 180px; border-radius: 50%; background: var(--primary); opacity: .1; top: 10%; left: 8%; animation: heroFloat 7s ease-in-out infinite; }
    .hero::after { content: ''; position: absolute; width: 120px; height: 120px; background: var(--accent); opacity: .12; top: 20%; right: 12%; transform: rotate(45deg); animation: heroFloat 9s ease-in-out infinite 1s; }
    .hero-shapes { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
    .hero-shape { position: absolute; opacity: .1; }
    .hero-shape-1 { width: 100px; height: 100px; border-radius: 50%; background: #8b5cf6; bottom: 15%; left: 15%; animation: heroFloat 8s ease-in-out infinite 2s; }
    .hero-shape-2 { width: 0; height: 0; border-left: 60px solid transparent; border-right: 60px solid transparent; border-bottom: 100px solid var(--primary); bottom: 25%; right: 8%; animation: heroFloat 6s ease-in-out infinite .5s; }
    .hero-shape-3 { width: 80px; height: 80px; background: var(--accent); transform: rotate(30deg); top: 55%; left: 5%; animation: heroFloat 10s ease-in-out infinite 1.5s; }
    @keyframes heroFloat { 0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); } 50% { transform: translateY(-20px) rotate(var(--r, 0deg)); } }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px; max-width: 1100px; margin: 0 auto; width: 100%; text-align: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.6rem, 7vw, 4.5rem); line-height: 1.15; margin-bottom: 16px; font-weight: 700; color: var(--primary); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 560px; margin: 0 auto 32px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--primary); color: #fff; font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--shadow-md); }
    .hero-cta:hover { transform: translateY(-3px) rotate(-1deg); box-shadow: 0 10px 30px rgba(236,72,153,.3); opacity: 1; color: #fff; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 700; margin-bottom: 40px; color: var(--primary); text-align: center; }

    /* ABOUT - quote + text side by side */
    .about-split { display: grid; grid-template-columns: 1fr 1.5fr; gap: 40px; align-items: start; max-width: 900px; margin: 0 auto; }
    .about-pullquote { font-family: var(--font-heading); font-size: 1.4rem; font-weight: 600; color: var(--primary); line-height: 1.5; padding: 24px; border-left: 4px solid var(--primary); background: #fff; border-radius: 16px; box-shadow: var(--shadow-sm); }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES - icon-top cards */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 960px; margin: 0 auto; }
    .service-card { padding: 32px 28px; background: #fff; border-radius: 24px; border: 2px dotted var(--primary); box-shadow: var(--shadow-sm); transition: transform .3s, box-shadow .3s; text-align: center; }
    .service-card:hover { box-shadow: var(--shadow-md); transform: translateY(-4px); }
    .service-icon { margin-bottom: 14px; display: flex; justify-content: center; }
    .icon-char { font-size: 32px; line-height: 1; color: var(--accent); }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 600; margin-bottom: 8px; color: var(--text); }
    .service-card p { color: var(--text-muted); font-size: .92rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--primary); background: var(--bg-alt); padding: 4px 14px; border-radius: 20px; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 20px; border: 3px dotted var(--accent); box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.04) rotate(1deg); }

    /* TESTIMONIALS - 3-col cards */
    .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .testimonial-card { background: #fff; padding: 28px 24px; border-radius: 20px; box-shadow: var(--shadow-sm); border: 2px solid var(--bg-alt); }
    .testimonial-card p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-card .t-author { margin-top: 12px; }
    .testimonial-card .t-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-card .t-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: #E5E5E5; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 750px; margin: 0 auto; }
    .faq-item { border-bottom: 2px dotted var(--primary); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 600; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 750px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 2px dotted var(--primary); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 600; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--primary); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 2px dotted var(--bg-alt); color: var(--text-muted); font-size: .95rem; text-align: center; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #fff; padding: 20px 0; border-bottom: 2px dotted var(--primary); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-heading); padding: 8px 22px; font-size: 1rem; font-weight: 600; border-radius: 50px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - split with accent bar */
    .contact-split { display: flex; gap: 0; max-width: 900px; margin: 0 auto; background: #fff; border-radius: 24px; overflow: hidden; box-shadow: var(--shadow-md); }
    .contact-info { flex: 1; padding: 32px 28px; display: flex; flex-direction: column; gap: 20px; }
    .contact-accent-bar { width: 6px; background: var(--primary); flex-shrink: 0; }
    .contact-form-wrap { flex: 1.2; padding: 32px 28px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; font-size: .85rem; color: var(--primary); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 2px dotted #E5E5E5; border-radius: 16px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--primary); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 600; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(236,72,153,.3); }

    /* FOOTER */
    .footer { background: #1F1F1F; padding: 40px 0; text-align: center; }
    .footer p { color: #A0A0A0; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,241,242,.98); border-bottom: 2px dotted var(--primary); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { grid-template-columns: 1fr; }
      .about-split { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-split { flex-direction: column; }
      .contact-accent-bar { width: 100%; height: 6px; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
      .hero::before, .hero::after { display: none; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-shapes">
      <div class="hero-shape hero-shape-1"></div>
      <div class="hero-shape hero-shape-2"></div>
      <div class="hero-shape hero-shape-3"></div>
    </div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
